import { SportsCard } from "../types";

// Simple in-memory cache
const queryCache = new Map<string, { timestamp: number, data: SportsCard[] }>();
const CACHE_TTL = 1000 * 60 * 60; // 1 hour

// BACKEND URL (Hardcoded for stability check)
const BACKEND_URL = "https://1mwkkhdk52.execute-api.ca-central-1.amazonaws.com";

/**
 * Robust JSON parser that handles Markdown code blocks and potential truncation.
 */
const parseResponse = (text: string): SportsCard[] => {
  try {
    // 1. Strip Markdown code blocks if present (common with AI models)
    const jsonString = text.replace(/```json\n?|```/g, '').trim();
    const data = JSON.parse(jsonString);
    // Validate array
    if (!Array.isArray(data)) throw new Error("API did not return a list of cards.");

    return data.map((item, index) => ({
      id: `card-${Date.now()}-${index}`,
      player: item.player || "Unknown Player",
      year: item.year || "Unknown Year",
      manufacturer: item.manufacturer || "",
      set: item.set || "",
      cardNumber: item.card_number || item.cardNumber || "",
      variant: item.variant || "Base",
      team: item.team || "",

      isGraded: item.is_graded || false,
      grader: item.grader,
      gradeScore: item.grade_score || item.gradeScore || "Raw",

      conditionDetails: {
        centering: item.condition_details?.centering || "Unknown",
        corners: item.condition_details?.corners || "Unknown",
        edges: item.condition_details?.edges || "Unknown",
        surface: item.condition_details?.surface || "Unknown",
        materialType: item.material_type || item.condition_details?.material_type || "Unknown",
        printForensics: item.condition_details?.print_forensics || "Unknown"
      },

      estimatedValue: {
        min: item.estimatedValue?.min || 0,
        max: item.estimatedValue?.max || 0,
        currency: item.estimatedValue?.currency || "USD"
      },
      listingDraft: {
        title: item.listingDraft?.title || "",
        description: item.listingDraft?.description || "",
        tags: item.listingDraft?.tags || []
      },
      marketData: item.marketData || [],
      verification_sources: item.verification_sources || item.verificationSources || []
    }));
  } catch (e) {
    throw new Error("Failed to read results.");
  }
};

/**
 * Retries an async operation with exponential backoff to handle 429 Rate Limits.
 */
async function retryOperation<T>(
  operation: () => Promise<T>,
  retries = 3,
  delay = 2000
): Promise<T> {
  try {
    return await operation();
  } catch (error: any) {
    const isRateLimit = error?.status === 429 ||
      error?.message?.includes('429') ||
      error?.message?.includes('Resource has been exhausted');

    if (isRateLimit && retries > 0) {
      console.warn(`Rate limit hit. Retrying in ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
      return retryOperation(operation, retries - 1, delay * 2);
    }
    throw error;
  }
}

/**
 * Step 1: Perform fast OCR (Stub for now, done on backend)
 */
export const performOCR = async (
  base64Image: string,
  mimeType: string,
  onStatusUpdate: (status: string) => void
): Promise<string[]> => {
  onStatusUpdate("Preparing image for analysis...");
  await new Promise(r => setTimeout(r, 500));
  return [];
};

/**
 * Step 2: Analyze Cards
 */
// Polling Helper
const pollJob = async (jobId: string, userId: string): Promise<any> => {
  const maxAttempts = 30; // 60s timeout
  const AUTH_TOKEN = "slablogic-alpha";

  for (let i = 0; i < maxAttempts; i++) {
    await new Promise(r => setTimeout(r, 2000)); // Wait 2s
    try {
      const res = await fetch(`${BACKEND_URL}/jobs/${jobId}?userId=${userId}&token=${AUTH_TOKEN}`);
      if (res.status === 200) {
        const data = await res.json();
        if (data.status === 'COMPLETE') {
          return data.result;
        } else if (data.status === 'FAILED') {
          throw new Error(data.error || 'Job Failed');
        }
        // If PENDING, continue loop
      }
    } catch (e) {
      // Background poll error - hidden from user console unless debugging
      if (process.env.NODE_ENV === 'development') {
        console.debug("Background poll check...");
      }
    }
  }
  throw new Error("Analysis Timed Out");
};

export const analyzeBookshelf = async (
  base64Images: string[],
  mimeType: string,
  ocrContext: string[],
  onStatusUpdate: (status: string) => void,
  tone: string = 'Casual',
  isTrial: boolean = false,
  scanType: 'quick' | 'pro' = 'pro'
): Promise<SportsCard[]> => {

  // Determine User Identity
  let userId = 'guest'; // Default fallback

  if (typeof window !== 'undefined') {
    const storedId = localStorage.getItem('slab_user_id');
    if (storedId) {
      userId = storedId;
    } else {
      // Generate new Guest ID
      userId = `guest_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      localStorage.setItem('slab_user_id', userId);
    }

    // Override if isTrial is FALSE (implying real user auth logic handled elsewhere, 
    // but for now this function is mostly for the public trial page)
    // If the caller explicitly passed isTrial=false, they likely are handling auth token.
    // However, for the 'Bypass' requested by user, we trust the ID they might have manually set ('VIP_USER')
  }

  // If forced trial param is passed, we still use the ID we determined
  // The backend handles the "VIP_USER" logic.


  try {
    // 1. Start Job
    onStatusUpdate("Starting Cloud Analysis...");
    const payload = {
      images: base64Images,
      mimeType,
      ocrContext,
      scanType,
      userId,
      tone // Pass existing params
    };

    const startRes = await fetch(`${BACKEND_URL}/jobs/start`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'slablogic-alpha'
      },
      body: JSON.stringify(payload)
    });

    if (startRes.status !== 202) {
      throw new Error(`Failed to start scan: ${startRes.status}`);
    }

    const { jobId } = await startRes.json();
    onStatusUpdate("Processing Scan (This may take 15-20s)...");

    // 2. Poll for Result
    const rawData = await pollJob(jobId, userId);

    // 3. Process Result - Backend now returns an array of cards
    // Handle both array format (new batch) and legacy single-object format
    const cardsArray = Array.isArray(rawData) ? rawData : [rawData];

    const cards = cardsArray.map((item, index) => parseSingleCardResponse(item, index));
    return cards;

  } catch (error: any) {
    console.error("Analysis Error:", error);
    let msg = "Analysis Service Encountered a Problem";
    if (error.message?.includes('402')) msg = "Free trial limit reached. Please upgrade to continue.";
    if (error.message?.includes('401')) msg = "Session expired. Please sign in again.";
    if (error.message?.includes('Timed Out')) msg = "Analysis took too long. Please try again.";

    return Promise.reject(msg);
  }
};

// Helper to parse card objects returned by worker
const parseSingleCardResponse = (item: any, index: number = 0): SportsCard => {
  // FIXED: Use camelCase to match backend schema
  return {
    id: `card-${Date.now()}-${index}`,
    player: item.player || "Unknown Player",
    year: item.year || "Unknown Year",
    manufacturer: item.manufacturer || "",
    set: item.set || "",
    cardNumber: item.cardNumber || item.card_number || "",
    variant: item.variant || "Base",
    team: item.team || "",

    isGraded: item.isGraded || item.is_graded || false,  // camelCase primary
    grader: item.grader,
    gradeScore: item.gradeScore || item.grade_score || "Raw",  // camelCase primary

    conditionDetails: {
      centering: item.conditionDetails?.centering || item.condition_details?.centering || "Unknown",
      corners: item.conditionDetails?.corners || item.condition_details?.corners || "Unknown",
      edges: item.conditionDetails?.edges || item.condition_details?.edges || "Unknown",
      surface: item.conditionDetails?.surface || item.condition_details?.surface || "Unknown",
      materialType: item.conditionDetails?.materialType || item.material_type || item.condition_details?.material_type || "Unknown",
      printForensics: item.conditionDetails?.printForensics || item.condition_details?.print_forensics || "Unknown"
    },

    estimatedValue: {
      min: item.estimatedValue?.min || 0,
      max: item.estimatedValue?.max || 0,
      currency: item.estimatedValue?.currency || "USD"
    },
    listingDraft: {
      title: item.listingDraft?.title || "",
      description: item.listingDraft?.description || "",
      tags: item.listingDraft?.tags || []
    },
    marketData: item.marketData || [],
    verification_sources: item.verification_sources || item.verificationSources || []
  };
};

export const identifyBookByText = async (
  query: string,
  onStatusUpdate: (status: string) => void,
  tone: string = 'Casual'
): Promise<SportsCard[]> => {
  const cacheKey = `text:${query.toLowerCase().trim()}:${tone}`;
  const cached = queryCache.get(cacheKey);

  if (cached && (Date.now() - cached.timestamp < CACHE_TTL)) {
    onStatusUpdate("Retrieved from cache...");
    await new Promise(r => setTimeout(r, 500));
    return cached.data;
  }

  return retryOperation(async () => {
    onStatusUpdate(`Searching for "${query}"...`);

    try {
      const response = await fetch(`${BACKEND_URL}/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'search_book',
          query: query,
          tone: tone,
          token: typeof window !== 'undefined' ? localStorage.getItem('shelf_shark_token') || 'shelf-shark-beta' : 'shelf-shark-beta'
        })
      });

      if (!response.ok) throw new Error("Search failed");

      const rawText = await response.text();
      const cards = parseResponse(rawText);

      if (cards.length > 0) {
        queryCache.set(cacheKey, { timestamp: Date.now(), data: cards });
      }

      return cards;

    } catch (e: any) {
      console.error("Backend Search Error", e);
      throw new Error("Failed to find card: " + e.message);
    }
  });
};